import { TrackedChange } from 'models/changes'
import { MutableRefObject } from 'react'
import {
    FetchFunctionRef,
    SuggestionsStoreActions,
    TrackChangesDataMap,
} from './types'

/**
 * Simplified TrackChangesUpdateService for track changes operations
 * Handles updating and detecting changes with straightforward error handling
 */
export class TrackChangesUpdateService {
    private suggestionsStore: SuggestionsStoreActions
    private fetchFunctionRef: FetchFunctionRef

    constructor(
        suggestionsStore: SuggestionsStoreActions,
        fetchFunctionRef: FetchFunctionRef
    ) {
        this.suggestionsStore = suggestionsStore
        this.fetchFunctionRef = fetchFunctionRef
    }

    /**
     * Updates track changes data
     */
    async updateTrackChanges(): Promise<void> {
        try {
            if (!this.fetchFunctionRef.current) {
                throw new Error('Fetch function not available')
            }

            await this.fetchFunctionRef.current()
        } catch (error) {
            console.error('Error updating track changes:', error)
            throw error
        }
    }

    /**
     * Detects new and modified track changes
     * @param prevTrackChangesDataRef - Reference to previous track changes data
     * @returns Map of unseen changes
     */
    getUnseenTrackChangesData(
        prevTrackChangesDataRef: MutableRefObject<TrackChangesDataMap>
    ): TrackChangesDataMap {
        const { trackChangesData } = this.suggestionsStore
        const prevTrackChangesData = prevTrackChangesDataRef.current || {}

        if (!trackChangesData || Object.keys(trackChangesData).length === 0) {
            return {}
        }

        const unseenChanges: TrackChangesDataMap = {}

        for (const [id, change] of Object.entries(trackChangesData)) {
            const prevChange = prevTrackChangesData[id]
            const isNewChange = !prevChange
            const isModifiedChange =
                prevChange && this.hasChangeBeenModified(prevChange, change)

            if (isNewChange || isModifiedChange) {
                unseenChanges[id] = change
            }
        }

        return unseenChanges
    }

    /**
     * Checks if a track change has been modified
     */
    private hasChangeBeenModified(
        prevChange: TrackedChange,
        currentChange: TrackedChange
    ): boolean {
        return (
            prevChange.changeDateTime !== currentChange.changeDateTime ||
            prevChange.status !== currentChange.status ||
            prevChange.changedText !== currentChange.changedText
        )
    }
}
